import { useEffect, useState } from "react";
import { Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ROLE_HELP = {
  owner: "Full control, including people and passwords",
  admin: "Everything except managing owners",
  editor: "Add and edit items",
  viewer: "Can only look",
};

/**
 * Desktop app only: add a teammate directly (no email invitations, no
 * self-sign-up there). Creates their login and shows the password once.
 */
export function AddMemberDialog({ open, onOpenChange, orgId, isOwner, onAdded }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) { setName(""); setEmail(""); setRole("editor"); setPassword(""); setResult(null); }
  }, [open]);

  const roles = isOwner ? ["owner", "admin", "editor", "viewer"] : ["editor", "viewer"];

  const submit = async (e) => {
    e?.preventDefault();
    if (password && password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/orgs/${orgId}/members`, {
        name: name.trim(), email: email.trim(), role_name: role,
        ...(password ? { password } : {}),
      });
      setResult(r.data);
      onAdded && onAdded();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Couldn't copy — select it and copy manually"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-member-dialog">
        {!result ? (
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle>Add member</DialogTitle>
              <DialogDescription>
                Creates their login on this computer. You'll get a password to give them.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="am-name">Name</Label>
                <Input id="am-name" autoFocus value={name} onChange={(e) => setName(e.target.value)}
                  data-testid="add-member-name" />
              </div>
              <div>
                <Label htmlFor="am-email">Email (used to sign in)</Label>
                <Input id="am-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="add-member-email" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="add-member-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r} value={r}>
                        <span className="capitalize">{r}</span>
                        <span className="text-muted-foreground"> — {ROLE_HELP[r]}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="am-pw">Password <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input id="am-pw" type="text" autoComplete="off" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Leave empty to generate one" data-testid="add-member-password" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={busy || !name.trim() || !email.trim()} data-testid="add-member-submit">
                {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Add member
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{result.email} added</DialogTitle>
              <DialogDescription>
                {result.password
                  ? "Give them these sign-in details. The password won't be shown again — they can change it under their profile."
                  : "They already had a login on this computer, so their existing password still works."}
              </DialogDescription>
            </DialogHeader>
            {result.password && (
              <div className="py-4 space-y-2 text-sm" data-testid="add-member-result">
                <div className="flex items-center gap-2">
                  <span className="w-20 text-muted-foreground">Email</span>
                  <code className="flex-1 font-mono">{result.email}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-20 text-muted-foreground">Password</span>
                  <code className="flex-1 rounded-md border border-border bg-muted px-3 py-2 font-mono text-base tracking-wide select-all"
                    data-testid="add-member-result-password">{result.password}</code>
                  <Button type="button" variant="outline" size="icon" onClick={() => copy(result.password)} aria-label="Copy password">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)} data-testid="add-member-done">Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
